import { Badge, Button, TabPanel, Tabs, type TabItem } from '@/shared/ui';
import { Link } from '@tanstack/react-router';
import { ArrowLeft } from 'lucide-react';
import { usePaymentSettingsEditor } from '../hooks/use-payment-settings-editor.hook';
import { PaymentGatewayTab } from './payment-gateway-tab.component';
import { PaymentMethodsCatalogTab } from './payment-methods-catalog-tab.component';
import { PaymentSettingsHistoryTab } from './payment-settings-history-tab.component';

export type PaymentSettingsTab = 'gateway' | 'catalog' | 'history';

const TABS: TabItem<PaymentSettingsTab>[] = [
  { value: 'gateway', label: 'Cobrança e formas' },
  { value: 'catalog', label: 'Catálogo de formas' },
  { value: 'history', label: 'Histórico de alterações' },
];

type PaymentSettingsEditorProps = {
  productCode: string;
  tab: PaymentSettingsTab;
  onTabChange: (tab: PaymentSettingsTab) => void;
};

/** Configurações > Produtos > Formas de pagamento > um produto. */
export function PaymentSettingsEditor({
  productCode,
  tab,
  onTabChange,
}: PaymentSettingsEditorProps) {
  const {
    settings,
    isLoading,
    error,
    refetch,
    productName,
    canEdit,
    status,
    updatedNote,
  } = usePaymentSettingsEditor(productCode);

  return (
    <div className='flex flex-col gap-4'>
      <Link
        to='/settings/products/payment-methods'
        className='flex w-fit items-center gap-1 text-sm text-alba-blue-500 hover:underline'
      >
        <ArrowLeft aria-hidden='true' className='h-4 w-4' />
        Formas de pagamento
      </Link>

      <div className='flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between'>
        <div className='min-w-0'>
          <h2 className='text-lg font-semibold text-alba-black-500'>
            {productName}{' '}
            <span className='text-sm font-normal text-alba-gray-500'>
              · {productCode}
            </span>
          </h2>
          {updatedNote && (
            <p className='mt-1 text-sm text-alba-gray-600'>{updatedNote}</p>
          )}
        </div>
        {status && <Badge tone={status.tone}>{status.label}</Badge>}
      </div>

      {isLoading && (
        <p className='py-8 text-center text-sm text-alba-gray-500'>
          Carregando...
        </p>
      )}

      {error && (
        <div className='flex flex-col items-center gap-3 py-8 text-sm text-alba-error-red-600'>
          <p>{error}</p>
          <Button
            type='button'
            variant='outline'
            size='md'
            onClick={() => void refetch()}
          >
            Tentar novamente
          </Button>
        </div>
      )}

      {settings && (
        <>
          {settings.gateway.validationErrors.length > 0 && (
            <div
              role='alert'
              className='rounded-lg border border-alba-error-red-400 bg-alba-error-red-400/10 p-3 text-sm text-alba-error-red-600'
            >
              A cobrança salva está inválida e a emissão a ignora:{' '}
              {settings.gateway.validationErrors.join(' ')}
            </div>
          )}

          <Tabs value={tab} onValueChange={onTabChange} items={TABS}>
            <TabPanel value='gateway'>
              <PaymentGatewayTab
                key={settings.gateway.version}
                settings={settings}
                productName={productName}
                canEdit={canEdit}
              />
            </TabPanel>
            <TabPanel value='catalog'>
              <PaymentMethodsCatalogTab settings={settings} canEdit={canEdit} />
            </TabPanel>
            <TabPanel value='history'>
              <PaymentSettingsHistoryTab productCode={productCode} />
            </TabPanel>
          </Tabs>
        </>
      )}
    </div>
  );
}
